import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, ViewChild } from '@angular/core';
import { Observable } from 'rxjs';
import { shareReplay, tap } from 'rxjs/operators';
import { NzTableQueryParams } from 'ng-zorro-antd/table';
import { environment } from '../../../../environments/environment';
import { IArticleVariant, ITableResults } from '../../../core/interfaces';
import { ISimpleListItem } from '../../../core/interfaces/ISimpleListItem';
import { NotificationService } from '../../../core/services';
import { TABLE_ROW_ESTIMATE_PX, tableScrollForViewport } from '../../../core/utils';
import { StoresService } from '../../stores/core/services/StoresService';
import { StoreStocksErrorResolverService } from '../core/error-resolvers/store-stocks-error-resolver.service';
import { StoreStocksService } from '../core/services/StoreStocks.service';

@Component({
    selector: 'app-lagerbestand-page',
    templateUrl: './lagerbestand-page.component.html',
    styleUrl: './lagerbestand-page.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class LagerbestandPageComponent {

  public data: ITableResults<IArticleVariant> = {
    results: [],
    info: {
      page: 1,
      results: 0,
      totalResults: 0,
      exportTableName: null
    }
  };
  public readonly stores$: Observable<ISimpleListItem[]>;
  public loading = false;
  public currentSearch = '';
  public pageSize = environment.defaultResultsPerPage;
  public hasSearched = false;
  public tableScroll: { x: string; y?: string } = { x: '1400px' };
  private storeCount = 0;

  @ViewChild('tableRef', { read: ElementRef }) tableHost: ElementRef<HTMLElement>;

  constructor(
    private readonly _cdr: ChangeDetectorRef,
    private readonly _storeService: StoresService,
    private readonly _storeStocksService: StoreStocksService<any>,
    private readonly _notifyService: NotificationService,
    private readonly _errorResolver: StoreStocksErrorResolverService,
  ) {
    this.stores$ = this._storeService.getSimple().pipe(
      tap(stores => {
        this.storeCount = stores.length;
        this.refreshTableScroll();
      }),
      shareReplay(1),
    );
  }

  @HostListener('window:resize')
  public onResize(): void {
    this.refreshTableScroll();
  }

  public search(val: string): void {
    this.currentSearch = (val || '').trim();
    this.data.info.page = 1;
    if (!this.currentSearch) {
      this.hasSearched = false;
      this.data = {
        results: [],
        info: {
          page: 1,
          results: 0,
          totalResults: 0,
          exportTableName: null
        }
      };
      this._cdr.markForCheck();
      return;
    }
    this.hasSearched = true;
    this.load(1, this.pageSize);
  }

  public onQueryParamsChange(params: NzTableQueryParams): void {
    if (!this.hasSearched) {
      return;
    }
    const { pageIndex, pageSize } = params;
    if (pageIndex === this.data.info.page && pageSize === this.pageSize) {
      return;
    }
    this.pageSize = pageSize;
    this.load(pageIndex, pageSize);
  }

  public onRefreshList(): void {
    if (!this.hasSearched) {
      return;
    }
    this.load(this.data.info.page || 1, this.pageSize);
  }

  public stockTotal(variant: IArticleVariant): number {
    return (variant.stocks ?? []).reduce((sum, stock) => sum + (stock.inStock ?? 0), 0);
  }

  private refreshTableScroll(): void {
    const tableEl = this.tableHost?.nativeElement
      ?? document.getElementById('lagerbestand-list-table');
    const next = tableScrollForViewport(`${540 + this.storeCount * 180}px`, {
      tableEl,
      rowCount: this.data.results?.length || 0,
      rowHeight: TABLE_ROW_ESTIMATE_PX,
    });
    if (next.x !== this.tableScroll.x || next.y !== this.tableScroll.y) {
      this.tableScroll = next;
      this._cdr.markForCheck();
    }
  }

  private load(pageIndex: number, pageSize: number): void {
    this.loading = true;
    this._cdr.markForCheck();
    this._storeStocksService.getStoreStocksBySearch(this.currentSearch, pageIndex, pageSize).subscribe({
      next: res => {
        this.loading = false;
        this.data = res;
        this.pageSize = pageSize;
        this.refreshTableScroll();
        this._cdr.markForCheck();
      },
      error: err => {
        this.loading = false;
        this._notifyService.pushError('Fehler', this._errorResolver.getMessage(err?.errorCode));
        this._cdr.markForCheck();
      }
    });
  }
}
